import { z } from "zod";
import { MAX_ABOUT, MAX_CHILDREN, MAX_CIRCLE_TITLE, MAX_MESSAGE_LENGTH, PASSWORD_MIN_LENGTH } from "./rules.js";
const trimmed = (min, max) => z.string().trim().min(min).max(max);
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
export const uuid = z.uuid();
// ---------- auth ----------
export const email = z.email().trim().toLowerCase().max(254);
export const password = z.string().min(PASSWORD_MIN_LENGTH).max(256);
export const signUpBody = z.object({ email, password });
export const signInBody = z.object({
    email,
    password: z.string().min(1).max(256),
    /** Required only for the admin account (2FA). */
    totp: z.string().regex(/^\d{6}$/).optional(),
});
export const oauthBody = z.object({
    provider: z.enum(["google", "apple"]),
    idToken: z.string().min(10),
    /** Apple only returns the name on first authorization. */
    firstName: z.string().max(80).optional(),
    lastName: z.string().max(80).optional(),
    nonce: z.string().max(200).optional(),
});
export const refreshBody = z.object({ refreshToken: z.string().min(20) });
export const adminSignInBody = z.object({ email, password: z.string().min(1), totp: z.string().regex(/^\d{6}$/) });
export const passwordResetRequestBody = z.object({ email });
export const code6 = z.string().trim().regex(/^\d{6}$/, "Enter the 6-digit code");
export const verifyEmailBody = z.object({ code: code6 });
export const passwordResetBody = z.object({ email, code: code6, password });
export const tokenPair = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    accessTokenExpiresAt: z.string(),
});
// ---------- onboarding / profile ----------
export const childInput = z.object({
    id: uuid.optional(),
    firstName: trimmed(1, 60),
    birthDate: isoDate,
});
export const onboardingBody = z.object({
    cohortCode: z.string().trim().toUpperCase().min(3).max(32),
    firstName: trimmed(1, 80),
    lastName: trimmed(1, 80),
    neighborhoodId: z.number().int().positive(),
    children: z.array(childInput).min(1).max(MAX_CHILDREN),
});
export const profilePatch = z.object({
    firstName: trimmed(1, 80).optional(),
    lastName: trimmed(1, 80).optional(),
    neighborhoodId: z.number().int().positive().optional(),
    homeConversationId: uuid.optional(),
    /** Empty string clears it. */
    about: z.string().trim().max(MAX_ABOUT).optional(),
    children: z.array(childInput).min(1).max(MAX_CHILDREN).optional(),
});
// ---------- chat ----------
export const conversationKind = z.enum(["village", "circle", "dm"]);
export const visibility = z.enum(["open", "closed"]);
export const createCircleBody = z.object({
    title: trimmed(1, MAX_CIRCLE_TITLE),
    visibility,
    inviteeIds: z.array(uuid).max(200).default([]),
});
export const sendMessageBody = z.object({
    clientMsgId: uuid,
    conversationId: uuid,
    kind: z.enum(["text", "photo"]).default("text"),
    body: z.string().max(MAX_MESSAGE_LENGTH).default(""),
    attachmentId: uuid.optional(),
    replyToId: uuid.optional(),
});
// ---------- events ----------
export const rsvpStatus = z.enum(["going", "maybe", "no"]);
export const rsvpBody = z.object({ status: rsvpStatus });
export const eventSuggestionBody = z.object({
    title: trimmed(3, 120),
    /** ISO date-time picked in the app; must be in the future. */
    startsAt: z.iso.datetime({ offset: true }).refine((s) => new Date(s).getTime() > Date.now(), "Choose a time in the future."),
    locationText: trimmed(3, 200),
    details: trimmed(3, 2000),
});
// ---------- co-op ----------
export const homeSafety = z.object({
    hasFirearms: z.boolean(),
    hasSmokers: z.boolean(),
    hasPool: z.boolean(),
    hasUnfencedWater: z.boolean(),
    hasDogs: z.boolean(),
    hasCats: z.boolean(),
    hasOtherPets: z.boolean(),
    otherPets: z.string().trim().max(200).default(""),
    hasTrampoline: z.boolean(),
    allAdultsVaccinated: z.boolean(),
    cprCertified: z.boolean(),
    hasCarSeat: z.boolean(),
});
/**
 * A mom's comfort list. Home rules apply to whichever home the sit happens in (checked in the right
 * direction per request); `sitterCprCertified` applies to the sitter as a person.
 */
export const requirementKeys = [
    "noFirearms",
    "noSmokers",
    "noPool",
    "noUnfencedWater",
    "noDogs",
    "noCats",
    "noOtherPets",
    "noTrampoline",
    "vaccinatedAdults",
    "sitterCprCertified",
];
export const requirementKey = z.enum(requirementKeys);
export const coopProfileBody = z.object({
    intro: trimmed(10, 1000),
    notes: z.string().trim().max(1000).default(""),
    servedAreaIds: z.array(z.number().int().min(1).max(77)).min(1).max(77),
    home: homeSafety,
    requirements: z.array(requirementKey).max(requirementKeys.length),
});
export const locationMode = z.enum(["mine", "sitter", "either"]);
export const careRequestBody = z
    .object({
    title: trimmed(2, 80),
    details: z.string().trim().max(2000).default(""),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    locationMode,
    childIds: z.array(uuid).min(1),
})
    .refine((r) => new Date(r.endsAt) > new Date(r.startsAt), { message: "End must be after start", path: ["endsAt"] })
    .refine((r) => new Date(r.endsAt).getTime() - new Date(r.startsAt).getTime() <= 14 * 3600_000, { message: "Requests can be up to 14 hours", path: ["endsAt"] });
export const careOfferBody = z.object({ note: z.string().trim().max(500).default("") });
export const confirmHoursBody = z.object({ hours: z.number().min(0).max(14) });
export const reportBody = z.object({
    subjectUserId: uuid,
    messageId: uuid.optional(),
    requestId: uuid.optional(),
    reason: z.enum(["safety", "harassment", "spam", "inappropriate", "other"]),
    details: z.string().trim().max(2000).default(""),
});
// ---------- places / ads ----------
export const placeSuggestionBody = z.object({
    name: trimmed(2, 120),
    address: trimmed(3, 200),
    why: z.string().trim().max(1000).default(""),
});
export const adEventBody = z.object({
    events: z
        .array(z.object({
        adId: uuid,
        kind: z.enum(["impression", "click", "redeem_click"]),
        placement: z.enum(["home_banner", "explore_sheet", "landing"]),
        sessionId: z.string().min(8).max(64),
        at: z.iso.datetime(),
    }))
        .min(1)
        .max(100),
});
// ---------- push ----------
export const registerDeviceBody = z.object({
    platform: z.enum(["ios", "android", "web"]),
    pushToken: z.string().min(10).max(500),
});
export const notificationCategory = z.enum([
    "messages",
    "circle_changes",
    "events",
    "coop_requests",
    "coop_updates",
    "promos",
]);
//# sourceMappingURL=schemas.js.map